// ORCA360 - Transcript Viewer Component
// Sprint 2, Step 4: Interactive transcript viewer with virtual scrolling
// Features: Search, speaker filters, color-coding, export, timestamps

import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { downloadTranscriptAsDocx } from '../lib/docxExport';

// NOTE: react-window removed temporarily due to Vite/Rollup CommonJS import issues
// TODO: Re-add virtual scrolling in future optimization (Sprint 3 or 4)
// For now, using standard scrolling which works fine for transcripts up to ~1000 segments

interface SpeakerSegment {
  id: string;
  speaker_label: string;
  speaker_role?: 'instructor' | 'trainee' | 'other' | 'unknown'; // Phase 2: Speaker role mapping
  start_time: string;
  end_time: string;
  text: string;
  speaker_confidence: number;
  word_count: number;
  transcript_id: string; // Track which transcript this segment belongs to
  transcript_index?: number; // Index in the array (0, 1, 2...) for display
}

interface Event {
  id: string;
  event_type: string;
  severity: 'critical' | 'warning' | 'positive' | 'info';
  start_time: string;
  context: string;
  matched_keyword: string;
  competencies: string[];
  confidence: number;
  dismissed: boolean;
}

interface TranscriptViewerProps {
  // Support both single and multiple transcripts (backward compatible)
  transcriptId?: string; // Deprecated: use transcriptIds instead
  transcriptIds?: string[]; // New: array of transcript IDs
  sessionId?: string;
  onTimestampClick?: (timestamp: string) => void;
  onClose?: () => void; // Optional: for modal/overlay mode
  highlightTimestamp?: string; // Timestamp to highlight and scroll to
  sessionInfo?: {
    trainee_name?: string;
    session_date?: string;
  };
}

export default function TranscriptViewer({
  transcriptId,
  transcriptIds,
  sessionId = '',
  onTimestampClick: _onTimestampClick,
  onClose,
  highlightTimestamp,
  sessionInfo,
}: TranscriptViewerProps) {
  // Normalize props: support both old (single transcriptId) and new (array transcriptIds) APIs
  const normalizedTranscriptIds = transcriptIds || (transcriptId ? [transcriptId] : []);

  const isModal = !!onClose;
  const [segments, setSegments] = useState<SpeakerSegment[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([]);
  const [fontSize, setFontSize] = useState(14); // px
  const [error, setError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const exportButtonRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    loadSegments();
    loadEvents();
  }, [normalizedTranscriptIds.join(','), sessionId]); // Join IDs for dependency tracking

  // Real-time updates: refresh segments when diarization updates arrive
  useEffect(() => {
    if (normalizedTranscriptIds.length === 0) return;

    // Subscribe to changes for all transcripts
    const channels = normalizedTranscriptIds.map((id) => {
      return supabase
        .channel(`segments-${id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'speaker_segments',
            filter: `transcript_id=eq.${id}`,
          },
          (_payload) => {
            // Reload segments on any insert/update/delete affecting any transcript
            loadSegments();
          }
        )
        .subscribe();
    });

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [normalizedTranscriptIds.join(',')]);

  // Error boundary for rendering issues
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('[TranscriptViewer] Render error:', event.error);
      setRenderError(event.error?.message || 'Unknown rendering error');
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Helper function to parse timestamps - extracted for reuse
  const parseTimestamp = (ts: string): number => {
    if (!ts) return 0;

    // Remove brackets if present: [00:44:06] -> 00:44:06
    const cleaned = ts.replace(/[\[\]]/g, '').trim();

    // Take start time if it's a range (MM:SS-MM:SS)
    const startPart = cleaned.split('-')[0]?.trim();
    if (!startPart) return 0;

    const parts = startPart.split(':');

    if (parts.length === 2) {
      // MM:SS format
      const minutes = Number(parts[0]);
      const seconds = Number(parts[1]);
      if (!isNaN(minutes) && !isNaN(seconds)) {
        return minutes * 60 + seconds;
      }
    } else if (parts.length === 3) {
      // HH:MM:SS format
      const hours = Number(parts[0]);
      const minutes = Number(parts[1]);
      const seconds = Number(parts[2]);
      if (!isNaN(hours) && !isNaN(minutes) && !isNaN(seconds)) {
        return hours * 3600 + minutes * 60 + seconds;
      }
    }
    return 0;
  };

  // Highlight and scroll to timestamp when provided
  useEffect(() => {
    if (!highlightTimestamp || segments.length === 0) return;

    console.log('[TranscriptViewer] Highlighting timestamp:', highlightTimestamp);

    const targetSeconds = parseTimestamp(highlightTimestamp);
    console.log('[TranscriptViewer] Target timestamp:', highlightTimestamp, '→', targetSeconds, 'seconds');

    if (targetSeconds === 0 && highlightTimestamp !== '0:00' && highlightTimestamp !== '00:00:00') {
      console.error('[TranscriptViewer] Failed to parse timestamp:', highlightTimestamp);
      return;
    }

    // Find segment that contains or is closest to this timestamp
    // Log segments near the target timestamp for debugging
    const nearbySegments = segments.filter(seg => {
      const start = parseTimestamp(seg.start_time);
      const end = parseTimestamp(seg.end_time);
      // Show segments within 30 seconds of target
      return Math.abs(start - targetSeconds) <= 30 || Math.abs(end - targetSeconds) <= 30;
    });

    if (nearbySegments.length > 0) {
      console.log('[TranscriptViewer] Segments near target timestamp', targetSeconds, 'seconds:');
      nearbySegments.forEach((seg) => {
        const start = parseTimestamp(seg.start_time);
        const end = parseTimestamp(seg.end_time);
        const withinRange = targetSeconds >= start && targetSeconds <= end;
        console.log(`  ${withinRange ? '✓' : ' '} ${seg.start_time} (${start}s) - ${seg.end_time} (${end}s): "${seg.text.substring(0, 60)}..."`);
      });
    }

    // Priority 1: Exact start-time match
    const exactStartMatch = segments.find(seg =>
      parseTimestamp(seg.start_time) === targetSeconds
    );

    let finalSegment = exactStartMatch ?? null;

    if (exactStartMatch) {
      console.log('[TranscriptViewer] ✓ Exact start-time match:', {
        segmentId: exactStartMatch.id,
        startTime: exactStartMatch.start_time,
        targetSeconds,
        speaker: exactStartMatch.speaker_label,
        text: exactStartMatch.text.substring(0, 80)
      });
    }

    // Priority 2: Range match (target falls within segment's [start, end])
    if (!finalSegment) {
      finalSegment = segments.find(seg => {
        const startSeconds = parseTimestamp(seg.start_time);
        const endSeconds = parseTimestamp(seg.end_time);
        return targetSeconds >= startSeconds && targetSeconds <= endSeconds;
      }) ?? null;

      if (finalSegment) {
        console.log('[TranscriptViewer] ✓ Range match (target within segment):', {
          segmentId: finalSegment.id,
          startTime: finalSegment.start_time,
          endTime: finalSegment.end_time,
          targetSeconds,
          speaker: finalSegment.speaker_label,
          text: finalSegment.text.substring(0, 80)
        });
      }
    }

    // Priority 3: Nearby segments fallback
    if (!finalSegment && segments.length > 0) {
      const candidateSegments = segments.filter(seg => {
        const segStart = parseTimestamp(seg.start_time);
        return segStart <= targetSeconds;
      });

      if (candidateSegments.length > 0) {
        finalSegment = candidateSegments.reduce((best, seg) => {
          const segStart = parseTimestamp(seg.start_time);
          const bestStart = parseTimestamp(best.start_time);
          return segStart > bestStart ? seg : best;
        });

        console.log('[TranscriptViewer] No exact/range match, using nearest segment before target:',
                    finalSegment.start_time, '(target:', targetSeconds, 's)');
      } else {
        finalSegment = segments.reduce((closest, seg) => {
          const segStart = parseTimestamp(seg.start_time);
          const closestStart = parseTimestamp(closest.start_time);
          return segStart < closestStart ? seg : closest;
        });

        console.log('[TranscriptViewer] No exact/range match, using earliest segment after target:',
                    finalSegment.start_time, '(target:', targetSeconds, 's)');
      }
    }

    if (finalSegment) {

      // IMPORTANT: Check if target segment's speaker is filtered out
      // If so, add the speaker to selectedSpeakers so the segment becomes visible
      if (!selectedSpeakers.includes(finalSegment.speaker_label)) {
        console.log('[TranscriptViewer] Target segment speaker is filtered out. Adding speaker:', finalSegment.speaker_label);
        setSelectedSpeakers(prev => [...prev, finalSegment.speaker_label]);
      }

      // Also check if search term would filter out this segment
      // If so, clear the search term to ensure the segment is visible
      if (searchTerm && !finalSegment.text.toLowerCase().includes(searchTerm.toLowerCase())) {
        console.log('[TranscriptViewer] Search term would hide target segment. Clearing search:', searchTerm);
        setSearchTerm('');
      }

      setActiveSegmentId(finalSegment.id);

      // Scroll to segment with slight delay to ensure DOM is ready (and filter update has propagated)
      setTimeout(() => {
        const segmentElement = segmentRefs.current.get(finalSegment.id);
        if (segmentElement) {
          // Position at the top of the visible area, accounting for sticky header
          const scrollOptions: ScrollIntoViewOptions = {
            behavior: 'smooth',
            block: 'center',  // Center in viewport for best visibility
            inline: 'nearest'
          };
          segmentElement.scrollIntoView(scrollOptions);
          console.log('[TranscriptViewer] Scrolled to segment:', finalSegment.id);
        } else {
          console.warn('[TranscriptViewer] Segment element not found in refs:', finalSegment.id,
                       'This may be due to search filter. Speaker filter was already checked.');
        }
      }, 150); // Slightly longer delay to allow filter state update and re-render

      // Clear highlight after 5 seconds
      const timer = setTimeout(() => {
        setActiveSegmentId(null);
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      console.error('[TranscriptViewer] No segment found for timestamp:', highlightTimestamp,
                    '→', targetSeconds, 'seconds. Total segments:', segments.length);
    }
  }, [highlightTimestamp, segments, selectedSpeakers, searchTerm]);

  const loadSegments = async () => {
    setLoading(true);
    setError(null);

    try {
      if (normalizedTranscriptIds.length === 0) {
        throw new Error('No transcript IDs provided');
      }

      console.log('[TranscriptViewer] Loading segments for', normalizedTranscriptIds.length, 'transcript(s):', normalizedTranscriptIds);

      // Load segments from ALL transcripts
      // NOTE: speaker_role is optional for backward compatibility (Phase 2 feature)
      let data, fetchError;

      try {
        // Try with speaker_role first (if migration 050 is applied)
        const result = await supabase
          .from('speaker_segments')
          .select('id, speaker_label, speaker_role, start_time, end_time, text, speaker_confidence, word_count, transcript_id')
          .in('transcript_id', normalizedTranscriptIds)
          .order('transcript_id', { ascending: true })
          .order('start_time', { ascending: true });

        data = result.data;
        fetchError = result.error;
      } catch (err) {
        // If speaker_role column doesn't exist, retry without it
        console.warn('[TranscriptViewer] speaker_role column not found, querying without it');
        const result = await supabase
          .from('speaker_segments')
          .select('id, speaker_label, start_time, end_time, text, speaker_confidence, word_count, transcript_id')
          .in('transcript_id', normalizedTranscriptIds)
          .order('transcript_id', { ascending: true })
          .order('start_time', { ascending: true });

        data = result.data;
        fetchError = result.error;
      }

      if (fetchError) {
        console.error('[TranscriptViewer] Error fetching segments:', fetchError);
        // Check if it's a column not found error
        if (fetchError.message?.includes('speaker_role') || fetchError.message?.includes('does not exist')) {
          // Retry without speaker_role column
          console.warn('[TranscriptViewer] Retrying query without speaker_role column...');
          const result = await supabase
            .from('speaker_segments')
            .select('id, speaker_label, start_time, end_time, text, speaker_confidence, word_count, transcript_id')
            .in('transcript_id', normalizedTranscriptIds)
            .order('transcript_id', { ascending: true })
            .order('start_time', { ascending: true });

          data = result.data;
          if (result.error) {
            console.error('[TranscriptViewer] Error on retry:', result.error);
            throw result.error;
          }
        } else {
          throw fetchError;
        }
      }

      console.log('[TranscriptViewer] Loaded segments:', data?.length || 0);

      if (!data || data.length === 0) {
        // No segments found - check if transcripts exist and create fallback
        const { data: transcriptData, error: transcriptError } = await supabase
          .from('transcripts')
          .select('id, original_text, anonymized_text, word_count')
          .in('id', normalizedTranscriptIds)
          .order('created_at', { ascending: true });

        if (transcriptError || !transcriptData || transcriptData.length === 0) {
          console.error('[TranscriptViewer] Transcripts not found:', transcriptError);
          throw new Error('Transcripts not found in database');
        }

        console.log('[TranscriptViewer] Transcripts exist but no segments. Creating fallback from text...');

        // Create fallback segments from full text of each transcript
        const fallbackSegments: SpeakerSegment[] = [];
        transcriptData.forEach((transcript, index) => {
          if (transcript.anonymized_text || transcript.original_text) {
            const text = transcript.anonymized_text || transcript.original_text || '';
            fallbackSegments.push({
              id: `fallback-${transcript.id}`,
              speaker_label: 'unknown',
              start_time: '00:00:00',
              end_time: '00:00:00',
              text: text,
              speaker_confidence: 0.0,
              word_count: transcript.word_count || text.split(/\s+/).length || 0,
              transcript_id: transcript.id,
              transcript_index: index,
            });
          }
        });

        if (fallbackSegments.length === 0) {
          throw new Error('Transcripts have no text content');
        }

        console.log('[TranscriptViewer] Created', fallbackSegments.length, 'fallback segment(s)');
        setSegments(fallbackSegments);
        setSelectedSpeakers(['unknown']);
        return;
      }

      // Add transcript index to each segment for display
      const segmentsWithIndex = data.map((segment) => {
        const transcriptIndex = normalizedTranscriptIds.indexOf(segment.transcript_id);
        return {
          ...segment,
          transcript_index: transcriptIndex,
        };
      });

      setSegments(segmentsWithIndex || []);

      // Auto-select all speakers
      const uniqueSpeakers = [...new Set(segmentsWithIndex?.map((s) => s.speaker_label) || [])];
      setSelectedSpeakers(uniqueSpeakers);
    } catch (err) {
      console.error('[TranscriptViewer] Error loading segments:', err);
      setError((err as Error).message || 'Failed to load transcript');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    if (!sessionId) {
      console.log('[TranscriptViewer] No sessionId provided, skipping event loading');
      return;
    }

    try {
      console.log('[TranscriptViewer] Loading events for session:', sessionId);

      const { data, error: fetchError } = await supabase
        .from('events')
        .select('*')
        .eq('session_id', sessionId)
        .eq('dismissed', false)
        .order('start_time');

      if (fetchError) {
        console.error('[TranscriptViewer] Error fetching events:', fetchError);
        // Don't throw - events are optional
        return;
      }

      console.log('[TranscriptViewer] Loaded events:', data?.length || 0);
      setEvents(data || []);
    } catch (err) {
      console.error('[TranscriptViewer] Error loading events:', err);
      // Don't set error state - events are optional
    }
  };

  // Filter and search segments
  const filteredSegments = useMemo(() => {
    return segments.filter((segment) => {
      const matchesSpeaker = selectedSpeakers.includes(segment.speaker_label);
      const matchesSearch =
        searchTerm === '' ||
        segment.text.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSpeaker && matchesSearch;
    });
  }, [segments, selectedSpeakers, searchTerm]);

  // Speaker colors (ORCA360 brand palette)
  // Updated Phase 2: Prioritize speaker_role over speaker_label
  const getSpeakerColor = (speaker: string, role?: string): string => {
    // Phase 2: Prioritize assigned speaker role
    if (role) {
      const roleColors: Record<string, string> = {
        instructor: 'bg-blue-100 text-blue-800 border-blue-400',
        trainee: 'bg-green-100 text-green-800 border-green-400',
        other: 'bg-gray-100 text-gray-700 border-gray-400',
        unknown: 'bg-yellow-100 text-yellow-800 border-yellow-400',
      };
      if (roleColors[role]) {
        return roleColors[role];
      }
    }

    // Fallback: Use speaker label for backward compatibility
    const speakerLower = speaker.toLowerCase();

    // Predefined roles (legacy labels)
    const roleColors: Record<string, string> = {
      instructor: 'bg-navy/10 text-navy border-orca-main/30',
      trainee_1: 'bg-orca-elevate/10 text-orca-elevate border-orca-elevate/30',
      trainee_2: 'bg-orca-vision/10 text-orca-vision border-orca-vision/30',
      atc: 'bg-steel/10 text-steel border-steel/30',
      other: 'bg-steel/10 text-steel border-steel/30',
      unknown: 'bg-gray-100 text-gray-700 border-gray-300',
    };

    // Check for predefined roles first
    if (roleColors[speakerLower]) {
      return roleColors[speakerLower];
    }
    
    // Handle SPEAKER_UNKNOWN (diarization couldn't identify)
    if (speakerLower === 'speaker_unknown') {
      return 'bg-amber-50 text-amber-700 border-amber-300'; // Amber/yellow for unknown
    }
    
    // Handle SPEAKER_00, SPEAKER_01, etc. from diarization
    const speakerMatch = speaker.match(/SPEAKER[_-]?(\d+)/i);
    if (speakerMatch && speakerMatch[1]) {
      const speakerNum = parseInt(speakerMatch[1], 10);
      const dynamicColors = [
        'bg-blue-50 text-blue-700 border-blue-300',      // SPEAKER_00
        'bg-green-50 text-green-700 border-green-300',   // SPEAKER_01
        'bg-purple-50 text-purple-700 border-purple-300', // SPEAKER_02
        'bg-orange-50 text-orange-700 border-orange-300', // SPEAKER_03
        'bg-pink-50 text-pink-700 border-pink-300',      // SPEAKER_04
        'bg-indigo-50 text-indigo-700 border-indigo-300', // SPEAKER_05
        'bg-teal-50 text-teal-700 border-teal-300',      // SPEAKER_06
        'bg-rose-50 text-rose-700 border-rose-300',      // SPEAKER_07
      ];
      return dynamicColors[speakerNum % dynamicColors.length] || 'bg-gray-100 text-gray-700 border-gray-300';
    }
    
    // Default fallback
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getSpeakerIcon = (speaker: string): string => {
    const speakerLower = speaker.toLowerCase();
    
    const icons: Record<string, string> = {
      instructor: '👨‍✈️',
      trainee_1: '👨‍💼',
      trainee_2: '👩‍💼',
      atc: '📡',
      other: '👤',
      unknown: '❓',
    };
    
    // Check for predefined roles first
    if (icons[speakerLower]) {
      return icons[speakerLower];
    }
    
    // Handle SPEAKER_UNKNOWN (diarization couldn't identify)
    if (speakerLower === 'speaker_unknown') {
      return '❔'; // Question mark for unknown speaker
    }
    
    // Handle SPEAKER_00, SPEAKER_01, etc. from diarization
    const speakerMatch = speaker.match(/SPEAKER[_-]?(\d+)/i);
    if (speakerMatch && speakerMatch[1]) {
      const speakerNum = parseInt(speakerMatch[1], 10);
      // Use numbered emojis for visual distinction
      const numberEmojis = ['0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣'];
      return numberEmojis[speakerNum] || '👤';
    }
    
    return '❓';
  };

  // Event helper functions
  const getSegmentEvents = (startTime: string): Event[] => {
    return events.filter((e) => e.start_time === startTime);
  };

  const getEventColor = (severity: 'critical' | 'warning' | 'positive' | 'info'): string => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 border border-red-300',
      warning: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      positive: 'bg-green-100 text-green-800 border border-green-300',
      info: 'bg-blue-100 text-blue-800 border border-blue-300',
    };
    const color = colors[severity];
    return color !== undefined ? color : 'bg-blue-100 text-blue-800 border border-blue-300';
  };

  const getEventIcon = (severity: 'critical' | 'warning' | 'positive' | 'info'): string => {
    const icons: Record<string, string> = {
      critical: '🔴',
      warning: '⚠️',
      positive: '✅',
      info: 'ℹ️',
    };
    const icon = icons[severity];
    return icon !== undefined ? icon : 'ℹ️';
  };

  const dismissEvent = async (eventId: string) => {
    try {
      await supabase
        .from('events')
        .update({ dismissed: true, dismissed_at: new Date().toISOString() })
        .eq('id', eventId);

      // Remove from local state
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      console.log('[TranscriptViewer] Event dismissed:', eventId);
    } catch (err) {
      console.error('[TranscriptViewer] Error dismissing event:', err);
    }
  };

  // Toggle speaker filter
  const toggleSpeaker = (speaker: string) => {
    setSelectedSpeakers((prev) =>
      prev.includes(speaker)
        ? prev.filter((s) => s !== speaker)
        : [...prev, speaker]
    );
  };

  // Get unique speakers
  const uniqueSpeakers = useMemo(() => {
    return [...new Set(segments.map((s) => s.speaker_label))];
  }, [segments]);

  // Highlight search results
  const highlightText = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    
    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 text-navy font-semibold rounded px-0.5">
          {part}
        </mark>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportButtonRef.current && !exportButtonRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  // Helper function for downloading files
  const downloadFile = (content: string, mimeType: string, extension: string) => {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = sessionInfo?.trainee_name 
      ? `transcript_${sessionInfo.trainee_name.replace(/\s+/g, '_')}_${sessionInfo.session_date}${extension}`
      : `transcript_${transcriptId}${extension}`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export to Plain Text (.txt)
  const exportToTXT = () => {
    const text = filteredSegments
      .map(
        (s) =>
          `[${s.start_time}] ${s.speaker_label.replace('_', ' ').toUpperCase()}: ${s.text}`
      )
      .join('\n\n');

    downloadFile(text, 'text/plain', '.txt');
    console.log('[TranscriptViewer] Exported to TXT');
  };

  // Export to Word Document (.docx)
  const exportToDOCX = async () => {
    try {
      const defaultDate = new Date().toISOString().split('T')[0];
      const traineeName: string = sessionInfo?.trainee_name || 'Trainee';
      const sessionDate: string = (sessionInfo?.session_date || defaultDate) as string;
      
      const transcriptData = {
        sessionInfo: {
          trainee_name: traineeName,
          session_date: sessionDate,
        },
        transcript_id: normalizedTranscriptIds[0] || 'unknown', // Use first transcript ID
        word_count: filteredSegments.reduce((sum, s) => sum + s.word_count, 0),
        created_at: new Date().toISOString(),
        segments: filteredSegments,
      };

      console.log('[TranscriptViewer] Exporting to DOCX with logo...');
      await downloadTranscriptAsDocx(transcriptData);
      console.log('[TranscriptViewer] DOCX export completed');
    } catch (error) {
      console.error('[TranscriptViewer] DOCX export error:', error);
      alert('Failed to export Word document. Please try TXT format instead.');
    }
  };

  // Export to JSON (.json)
  const exportToJSON = () => {
    const data = {
      session_id: sessionId,
      transcript_ids: normalizedTranscriptIds, // Export all transcript IDs
      session_info: sessionInfo,
      exported_at: new Date().toISOString(),
      segments: filteredSegments.map(s => ({
        id: s.id,
        speaker: s.speaker_label,
        start_time: s.start_time,
        end_time: s.end_time,
        text: s.text,
        word_count: s.word_count,
        speaker_confidence: s.speaker_confidence,
      })),
      statistics: {
        total_segments: filteredSegments.length,
        total_words: filteredSegments.reduce((sum, s) => sum + s.word_count, 0),
        unique_speakers: [...new Set(filteredSegments.map(s => s.speaker_label))],
        duration: (() => {
          const first = filteredSegments[0];
          const last = filteredSegments[filteredSegments.length - 1];
          return first && last ? `${first.start_time} - ${last.end_time}` : 'N/A';
        })(),
      },
    };

    const json = JSON.stringify(data, null, 2);
    downloadFile(json, 'application/json', '.json');
    console.log('[TranscriptViewer] Exported to JSON');
  };

  // Export to CSV (.csv)
  const exportToCSV = () => {
    // CSV header
    const header = 'Start Time,End Time,Speaker,Text,Word Count,Confidence\n';

    // CSV rows (properly escape quotes and newlines)
    const rows = filteredSegments.map(s => {
      const escapedText = s.text.replace(/"/g, '""').replace(/\n/g, ' ').replace(/\r/g, '');
      return `"${s.start_time}","${s.end_time}","${s.speaker_label}","${escapedText}",${s.word_count},${s.speaker_confidence}`;
    }).join('\n');

    const csv = header + rows;
    downloadFile(csv, 'text/csv', '.csv');
    console.log('[TranscriptViewer] Exported to CSV');
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl/Cmd + F for search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        document.getElementById('transcript-search')?.focus();
      }

      // Ctrl/Cmd + +/- for font size
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        setFontSize((prev) => Math.min(24, prev + 2));
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        setFontSize((prev) => Math.max(10, prev - 2));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Debug logging
  console.log('[TranscriptViewer] Render - segments:', segments.length, 'loading:', loading, 'error:', error, 'renderError:', renderError);

  // Render error display (if any JavaScript error occurred)
  if (renderError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-semibold">❌ Component Error</p>
        <p className="text-red-600 text-sm mt-2">{renderError}</p>
        <p className="text-red-500 text-xs mt-2">
          Check the browser console (F12) for details
        </p>
        <button
          onClick={() => {
            setRenderError(null);
            loadSegments();
          }}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          🔄 Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-white rounded-lg shadow-md">
          <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
          <p className="mt-2 text-steel">Loading transcript...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-semibold">Failed to load transcript</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadSegments}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
            </button>
      </div>
    );
  }

  if (segments.length === 0) {
  return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-12 text-center">
        <p className="text-yellow-800 text-lg font-semibold">⚠️ No Transcript Segments Found</p>
        <p className="text-yellow-700 text-sm mt-2">
          The transcript text exists, but speaker segments weren't created.
        </p>
        <p className="text-yellow-600 text-xs mt-2">
          This may happen if the Whisper API didn't return time-stamped chunks.
          <br />
          Check the browser console for details or re-run transcription.
        </p>
            <button
          onClick={loadSegments}
          className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
            >
          🔄 Retry Loading
            </button>
          </div>
    );
  }

  const content = (
    <div className={`bg-white rounded-lg shadow-md flex flex-col min-h-0 ${isModal ? 'flex-1' : 'h-full'}`}>
      {/* Header Controls - Fixed/Pinned */}
      <div className="bg-white border-b border-steel/20 p-4 space-y-3 relative flex-shrink-0 sticky top-0 z-20 shadow-sm">
        {/* Search Bar & Controls */}
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <input
              id="transcript-search"
              type="text"
              placeholder="Search transcript... (Ctrl+F)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-steel/30 rounded-md text-navy placeholder-steel/50 focus:outline-none focus:ring-2 focus:ring-orca-elevate"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-steel/50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          
          {/* Font Size Controls */}
          <div className="flex items-center space-x-2 border border-steel/30 rounded-md px-2 py-1">
            <button
              onClick={() => setFontSize((prev) => Math.max(10, prev - 2))}
              className="px-2 py-1 text-sm font-medium text-steel hover:text-navy transition-colors"
              title="Decrease font size (Ctrl+-)"
            >
              A-
            </button>
            <span className="text-xs text-steel font-mono">{fontSize}px</span>
            <button
              onClick={() => setFontSize((prev) => Math.min(24, prev + 2))}
              className="px-2 py-1 text-sm font-medium text-steel hover:text-navy transition-colors"
              title="Increase font size (Ctrl++)"
            >
              A+
            </button>
          </div>

          {/* Export Dropdown */}
          <div className="relative" ref={exportButtonRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="px-3 py-2 text-sm font-medium text-navy bg-white border border-steel/30 rounded-md hover:bg-white transition-colors flex items-center space-x-2"
              title="Export transcript in various formats"
            >
              <span>📥</span>
              <span>Export</span>
              <span className={`ml-1 transition-transform text-xs ${showExportMenu ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>

            {/* Dropdown Menu */}
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-steel/30 rounded-md shadow-xl z-[100] overflow-hidden">
                <button
                  onClick={() => {
                    exportToTXT();
                    setShowExportMenu(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-navy hover:bg-white transition-colors flex items-center space-x-3 border-b border-steel/10"
                  title="Download as plain text file"
                >
                  <span className="text-lg">📄</span>
                  <div>
                    <div className="font-medium">Plain Text</div>
                    <div className="text-xs text-steel">.txt format</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    exportToDOCX();
                    setShowExportMenu(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-navy hover:bg-white transition-colors flex items-center space-x-3 border-b border-steel/10"
                  title="Download as Word document"
                >
                  <span className="text-lg">📝</span>
                  <div>
                    <div className="font-medium">Word Document</div>
                    <div className="text-xs text-steel">.docx format with branding</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    exportToCSV();
                    setShowExportMenu(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-navy hover:bg-white transition-colors flex items-center space-x-3 border-b border-steel/10"
                  title="Download as spreadsheet"
                >
                  <span className="text-lg">📊</span>
                  <div>
                    <div className="font-medium">Spreadsheet</div>
                    <div className="text-xs text-steel">.csv format for Excel</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    exportToJSON();
                    setShowExportMenu(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-navy hover:bg-white transition-colors flex items-center space-x-3"
                  title="Download as JSON data"
                >
                  <span className="text-lg">🔧</span>
                  <div>
                    <div className="font-medium">JSON Data</div>
                    <div className="text-xs text-steel">.json format for APIs</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Speaker Legend & Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-steel mr-2">Speakers:</span>
          {uniqueSpeakers.map((speaker) => (
            <button
              key={speaker}
              onClick={() => toggleSpeaker(speaker)}
              className={`px-3 py-1 text-xs font-semibold rounded-md border transition-all ${
                selectedSpeakers.includes(speaker)
                  ? getSpeakerColor(speaker)
                  : 'bg-slate-100 text-slate-400 border-slate-200 opacity-50'
              }`}
              title={`${selectedSpeakers.includes(speaker) ? 'Hide' : 'Show'} ${speaker}`}
            >
              {getSpeakerIcon(speaker)} {speaker.replace('_', ' ').toUpperCase()}
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-steel">
          <span>
            Showing {filteredSegments.length} of {segments.length} segments
            {normalizedTranscriptIds.length > 1 && (
              <span className="ml-2 text-orca-elevate font-semibold">
                • {normalizedTranscriptIds.length} transcripts
              </span>
            )}
          </span>
          <span>{uniqueSpeakers.length} speakers</span>
        </div>
      </div>

      {/* Transcript Segments - Scrollable */}
      <div className="bg-white overflow-y-auto flex-1 min-h-0">
        {filteredSegments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-steel text-lg">No segments match your filters</p>
            <p className="text-steel/70 text-sm mt-1">
              Try adjusting your search or speaker filters
            </p>
          </div>
        ) : (
          filteredSegments.map((segment, index) => {
            // Check if we need to show a transcript separator (when transcript changes)
            const prevSegment = index > 0 ? filteredSegments[index - 1] : null;
            const showSeparator = index === 0 ||
              (prevSegment && prevSegment.transcript_id !== segment.transcript_id);

            return (
              <div key={segment.id}>
                {/* Transcript Separator */}
                {showSeparator && normalizedTranscriptIds.length > 1 && (
                  <div className="bg-gradient-to-r from-orca-elevate/10 via-orca-elevate/20 to-orca-elevate/10 border-y border-orca-elevate/30 px-4 py-3 sticky top-0 z-10 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-orca-elevate">
                          📄 Transcript {(segment.transcript_index || 0) + 1}
                        </span>
                        <span className="text-xs text-steel">
                          {segment.transcript_index === 0 && '(Briefing)'}
                          {segment.transcript_index === normalizedTranscriptIds.length - 1 &&
                           segment.transcript_index > 0 && '(Debriefing)'}
                          {segment.transcript_index !== 0 &&
                           segment.transcript_index !== normalizedTranscriptIds.length - 1 &&
                           '(Session)'}
                        </span>
                      </div>
                      <span className="text-xs text-steel/70 font-mono">
                        ID: {segment.transcript_id?.slice(0, 8) || 'unknown'}...
                      </span>
                    </div>
                  </div>
                )}

                {/* Segment */}
                <div
                  ref={(el) => {
                    if (el) segmentRefs.current.set(segment.id, el);
                    else segmentRefs.current.delete(segment.id);
                  }}
                  className={`border-b border-slate-100 p-3 hover:bg-white transition-colors ${
                    getSpeakerColor(segment.speaker_label, segment.speaker_role)
                  } ${
                    searchTerm && segment.text.toLowerCase().includes(searchTerm.toLowerCase())
                      ? 'bg-yellow-50'
                      : ''
                  } ${
                    activeSegmentId === segment.id
                      ? 'bg-yellow-200 ring-2 ring-yellow-400'
                      : ''
                  }`}
                >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-xs text-steel/60 font-mono min-w-[60px]">
                  {segment.start_time}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">
                      {getSpeakerIcon(segment.speaker_label)} {segment.speaker_label.replace('_', ' ').toUpperCase()}
                    </span>
                    {/* Phase 2: Speaker Role Badge */}
                    {segment.speaker_role && segment.speaker_role !== 'unknown' && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                        segment.speaker_role === 'instructor' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                        segment.speaker_role === 'trainee' ? 'bg-green-100 text-green-800 border-green-300' :
                        'bg-gray-100 text-gray-700 border-gray-300'
                      }`}>
                        {segment.speaker_role === 'instructor' ? '👨‍🏫 Instructor' :
                         segment.speaker_role === 'trainee' ? '👨‍✈️ Trainee' :
                         segment.speaker_role === 'other' ? 'Other' : ''}
                      </span>
                    )}
                    {segment.speaker_confidence < 0.8 && (
                      <span className="text-xs text-yellow-600" title={`Confidence: ${Math.round(segment.speaker_confidence * 100)}%`}>
                        ⚠️ Low confidence
                      </span>
                    )}
                  </div>
                  <p className="text-navy leading-relaxed" style={{ fontSize: `${fontSize}px` }}>
                    {highlightText(segment.text, searchTerm)}
                  </p>
                  
                  {/* Event Badges */}
                  {getSegmentEvents(segment.start_time).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {getSegmentEvents(segment.start_time).map((event) => (
                        <div
                          key={event.id}
                          className={`group relative px-2 py-1 text-xs font-semibold rounded-md ${getEventColor(event.severity)}`}
                          title={`${event.context}\n\nConfidence: ${Math.round(event.confidence * 100)}%\nCompetencies: ${event.competencies.join(', ')}`}
                        >
                          <span className="flex items-center gap-1">
                            {getEventIcon(event.severity)} {event.event_type.replace('_', ' ').toUpperCase()}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissEvent(event.id);
                              }}
                              className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs hover:text-red-600"
                              title="Dismiss event"
                            >
                              ✕
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  // If modal mode, wrap in overlay
  if (isModal) {
    try {
      return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col relative overflow-hidden">
            {/* Modal Header */}
            <div className="bg-navy text-white p-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl font-semibold">Session Transcript</h2>
                {sessionInfo && (
                  <p className="text-sm text-white/70 mt-1">
                    {sessionInfo.trainee_name || 'Trainee'} • {sessionInfo.session_date || 'Date'}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors text-2xl font-bold"
                title="Close"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
              {content}
            </div>
          </div>
        </div>
      );
    } catch (err) {
      console.error('[TranscriptViewer] Modal render error:', err);
      return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
            <p className="text-red-700 font-semibold">❌ Failed to render modal</p>
            <p className="text-red-600 text-sm mt-2">{(err as Error).message}</p>
          <button
            onClick={onClose}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Close
          </button>
        </div>
      </div>
      );
    }
  }

  // Otherwise, return inline content
  try {
    return content;
  } catch (err) {
    console.error('[TranscriptViewer] Content render error:', err);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-semibold">❌ Failed to render transcript</p>
        <p className="text-red-600 text-sm mt-2">{(err as Error).message}</p>
    </div>
  );
  }
}


